import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useGetFireDrillQuery,
  useUpdateFireDrillMutation,
  useGetUploadUrlMutation,
} from '../../store/api/localApi';
import { FireDrillIssueCategory, FireDrillScope, FireDrillType } from '../../types';
import { useToast } from '../../components/Toast';
import { ScopedLink, useScopedPath } from '../../components/ScopedLink';
import { UploadingPhoto } from '../../components/observations/ObservationPhotoUpload';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function labelForType(type: FireDrillType): string {
  switch (type) {
    case FireDrillType.ANNOUNCED:
      return 'Announced';
    case FireDrillType.UNANNOUNCED:
      return 'Unannounced';
    case FireDrillType.EVAC_REAL:
      return 'Real evacuation';
  }
}

function labelForScope(scope: FireDrillScope): string {
  return scope === FireDrillScope.LOCATION ? 'Full location' : 'Class only';
}

function labelForIssueCategory(cat: FireDrillIssueCategory): string {
  switch (cat) {
    case FireDrillIssueCategory.DOOR:
      return 'Door';
    case FireDrillIssueCategory.ALARM:
      return 'Alarm';
    case FireDrillIssueCategory.ROUTE:
      return 'Route';
    case FireDrillIssueCategory.STAFF:
      return 'Staff';
    case FireDrillIssueCategory.CHILD:
      return 'Child';
    case FireDrillIssueCategory.EQUIPMENT:
      return 'Equipment';
    case FireDrillIssueCategory.OTHER:
      return 'Other';
  }
}

const ISSUE_CATEGORIES = Object.values(FireDrillIssueCategory);
const TEXT_LIMIT = 2000;
const MAX_PHOTOS = 4;

// ─── WrapUpScreen ─────────────────────────────────────────────────────────────

const WrapUpScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scopedPath = useScopedPath();
  const { showSuccess, showError } = useToast();

  const { data: drillData, isLoading } = useGetFireDrillQuery(id!, { skip: !id });
  const drill = drillData?.drill;

  const [updateFireDrill, { isLoading: isSaving }] = useUpdateFireDrillMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();

  // ── Issues state ─────────────────────────────────────────────────────────────
  const [pendingIssues, setPendingIssues] = useState<
    { category: FireDrillIssueCategory; description: string }[]
  >([]);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [newIssueCategory, setNewIssueCategory] = useState<FireDrillIssueCategory>(
    FireDrillIssueCategory.OTHER
  );
  const [newIssueDescription, setNewIssueDescription] = useState('');

  // ── Notes state ───────────────────────────────────────────────────────────────
  const [notes, setNotes] = useState('');

  // ── Photos state ──────────────────────────────────────────────────────────────
  // Photos are uploaded to S3 via the presign endpoint but are not persisted
  // on the FireDrill entity in v1 (no `photos` field on FireDrill type yet).
  // The upload still happens so the files land in S3; the reference is deferred
  // to v1.1 when the backend adds a photos field.
  const [photos, setPhotos] = useState<UploadingPhoto[]>([]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleConfirmIssue = () => {
    if (!newIssueDescription.trim()) return;
    setPendingIssues((prev) => [
      ...prev,
      { category: newIssueCategory, description: newIssueDescription.trim() },
    ]);
    setNewIssueCategory(FireDrillIssueCategory.OTHER);
    setNewIssueDescription('');
    setShowAddIssue(false);
  };

  const handleRemoveIssue = (index: number) => {
    setPendingIssues((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || !drill) return;
    const slots = MAX_PHOTOS - photos.length;
    const fresh = Array.from(files).slice(0, slots);

    for (const file of fresh) {
      const previewUrl = URL.createObjectURL(file);
      const next: UploadingPhoto = {
        s3Key: '',
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
        uploading: true,
      };
      setPhotos((p) => [...p, next]);
      try {
        const { uploadUrl, s3Key } = await getUploadUrl({
          purpose: 'FIRE_DRILL_PHOTO',
          contextId: drill.id,
          mimeType: file.type,
          sizeBytes: file.size,
          fileName: file.name,
        }).unwrap();
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
        setPhotos((p) =>
          p.map((q) => (q.previewUrl === previewUrl ? { ...q, s3Key, uploading: false } : q))
        );
      } catch {
        setPhotos((p) =>
          p.map((q) =>
            q.previewUrl === previewUrl ? { ...q, uploading: false, error: 'Failed' } : q
          )
        );
      }
    }
  };

  const handleSave = async () => {
    if (!drill || isSaving) return;
    const trimmedNotes = notes.trim();
    const hasNotes = trimmedNotes.length > 0;
    const hasNewIssues = pendingIssues.length > 0;

    // Skip the PUT entirely if nothing changed — the backend requires at
    // least one field per request, so an empty save would 400. Just go
    // to the log; the drill itself was already completed by the LIVE
    // screen before we got here.
    if (!hasNotes && !hasNewIssues) {
      navigate(scopedPath('/fire-drills'));
      return;
    }

    try {
      await updateFireDrill({
        id: drill.id,
        notes: hasNotes ? trimmedNotes : undefined,
        issuesToAdd: hasNewIssues ? pendingIssues : undefined,
      }).unwrap();
      showSuccess('Drill record saved');
      navigate(scopedPath('/fire-drills'));
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to save');
    }
  };

  // ── Loading / not found ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Loading drill…
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Drill not found.</p>
        <ScopedLink to="/fire-drills" className="text-primary-600 hover:underline">
          ← Back to fire drills
        </ScopedLink>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────────

  const accountedCount = drill.attendees.filter(
    (a) => a.accountedFor || a.unaccountedReason
  ).length;
  const totalCount = drill.attendees.length;

  const anyUploading = photos.some((p) => p.uploading);

  // ── Main render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="wrap-up-screen">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-lg font-bold text-gray-900">Drill wrap-up</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {labelForType(drill.type)} · {labelForScope(drill.scope)}
            {drill.className ? ` · ${drill.className}` : ''}
          </p>
        </div>
      </header>

      {/* Summary card */}
      <div className="max-w-2xl w-full mx-auto px-4 pt-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {drill.evacuationSeconds != null && (
              <div>
                <p className="text-xs text-gray-500">Total time</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDuration(drill.evacuationSeconds)}
                </p>
              </div>
            )}
            {totalCount > 0 && (
              <div>
                <p className="text-xs text-gray-500">Accounted</p>
                <p className="text-lg font-semibold text-gray-900">
                  {accountedCount} / {totalCount}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className="text-sm font-medium text-gray-700 capitalize">
                {drill.status.toLowerCase().replace('_', ' ')}
              </p>
            </div>
          </div>
          {drill.assemblyPoint && (
            <p className="text-xs text-gray-400 mt-3">
              Assembly: {drill.assemblyPoint} · Route: {drill.exitRoute}
            </p>
          )}
        </div>
      </div>

      {/* Main form area */}
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-4 space-y-6">
        {/* Issues section */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-700">Issues</h2>
            {!showAddIssue && (
              <button
                type="button"
                data-testid="wrap-up-add-issue"
                onClick={() => setShowAddIssue(true)}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add issue
              </button>
            )}
          </div>

          {/* Existing issues from saved drill */}
          {drill.issues.length > 0 && (
            <ul className="space-y-2 mb-3">
              {drill.issues.map((issue) => (
                <li
                  key={issue.id}
                  className="flex items-start gap-2 bg-white rounded-md border border-gray-200 px-3 py-2"
                >
                  <span className="inline-block text-xs font-medium bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 mt-0.5 shrink-0">
                    {labelForIssueCategory(issue.category)}
                  </span>
                  <span className="text-sm text-gray-800">{issue.description}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Pending (unsaved) issues */}
          {pendingIssues.length > 0 && (
            <ul className="space-y-2 mb-3">
              {pendingIssues.map((issue, index) => (
                <li
                  key={index}
                  data-testid={`wrap-up-pending-issue-${index}`}
                  className="flex items-start gap-2 bg-amber-50 rounded-md border border-amber-200 px-3 py-2"
                >
                  <span className="inline-block text-xs font-medium bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 mt-0.5 shrink-0">
                    {labelForIssueCategory(issue.category)}
                  </span>
                  <span className="flex-1 text-sm text-gray-800">{issue.description}</span>
                  <button
                    type="button"
                    data-testid={`wrap-up-pending-issue-${index}-remove`}
                    onClick={() => handleRemoveIssue(index)}
                    className="shrink-0 text-gray-400 hover:text-gray-600"
                    aria-label="Remove issue"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Add-issue inline form */}
          {showAddIssue && (
            <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
              <select
                data-testid="wrap-up-issue-category"
                value={newIssueCategory}
                onChange={(e) => setNewIssueCategory(e.target.value as FireDrillIssueCategory)}
                className="block w-full rounded-md border-gray-300 text-sm"
              >
                {ISSUE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {labelForIssueCategory(cat)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                data-testid="wrap-up-issue-description"
                value={newIssueDescription}
                onChange={(e) => setNewIssueDescription(e.target.value)}
                placeholder="Describe the issue…"
                maxLength={500}
                className="block w-full rounded-md border-gray-300 text-sm"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddIssue(false);
                    setNewIssueDescription('');
                    setNewIssueCategory(FireDrillIssueCategory.OTHER);
                  }}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="wrap-up-issue-confirm"
                  onClick={handleConfirmIssue}
                  disabled={!newIssueDescription.trim()}
                  className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {drill.issues.length === 0 && pendingIssues.length === 0 && !showAddIssue && (
            <p className="text-sm text-gray-400">No issues recorded.</p>
          )}
        </section>

        {/* Notes section */}
        <section>
          <label
            htmlFor="wrap-up-notes-input"
            className="block text-sm font-semibold text-gray-700 mb-1"
          >
            Notes
          </label>
          <textarea
            id="wrap-up-notes-input"
            data-testid="wrap-up-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, TEXT_LIMIT))}
            rows={5}
            placeholder="Add any additional notes about this drill…"
            className="block w-full rounded-md border-gray-300 resize-y text-sm"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {notes.length} / {TEXT_LIMIT}
          </p>
        </section>

        {/* Photos section */}
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Photos (optional)</h2>

          {/* Thumbnails */}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {photos.map((p, i) => (
                <div
                  key={p.previewUrl}
                  data-testid={`wrap-up-photo-${i}`}
                  className="relative h-20 w-20 rounded-md overflow-hidden border border-gray-200"
                >
                  <img src={p.previewUrl} alt="" className="object-cover w-full h-full" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {p.error && (
                    <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center text-xs text-white">
                      {p.error}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      URL.revokeObjectURL(p.previewUrl);
                      setPhotos((prev) => prev.filter((_, idx) => idx !== i));
                    }}
                    className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5"
                    aria-label="Remove photo"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-700" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload trigger */}
          {photos.length < MAX_PHOTOS && (
            <label className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              <PhotoIcon className="h-4 w-4" />
              Add photo
              <input
                data-testid="wrap-up-photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </label>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {photos.length} / {MAX_PHOTOS} photos
            {/* Note: photo S3 keys are uploaded but not yet persisted on FireDrill (v1 gap).
                References will be stored when the backend adds a photos field in v1.1. */}
          </p>
        </section>
      </main>

      {/* Sticky footer */}
      <footer className="bg-white border-t border-gray-200 sticky bottom-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-end gap-3">
          <ScopedLink
            to="/fire-drills"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </ScopedLink>
          <button
            type="button"
            data-testid="wrap-up-save"
            onClick={handleSave}
            disabled={isSaving || anyUploading}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving…' : 'Save drill record'}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default WrapUpScreen;
